import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { fmtCHF2, fmtPct } from '../utils/format';
import { LigneEmploye, ScenarioBudget, Tag, MOIS_LABELS } from '../types';
import { ChoixTagScenario } from './TagScenarioRevenu';
import { IMPACT_VIDE, fusionnerImpacts, impactPortee, listerMois } from '../utils/portee';

type Portee = 'sansFin' | 'ceMois' | 'jusqua';

interface Props {
  ligne: LigneEmploye; // état de la grille AVANT édition (sert aux valeurs de retour)
  mois: number; // 1..12
  annee: number;
  // Scénario de lecture courant : une hypothèse projetée s'y rattache par défaut
  scenarioId: number | null;
  scenarios: ScenarioBudget[];
  ancre: { top: number; bottom: number; left: number; right: number };
  onFermer: () => void;
  onModifie: () => void;
  onOuvrirFiche: () => void; // bascule vers le grand modal (fiche complète)
}

const LARGEUR = 360;

function iso(annee: number, mois: number): string {
  return `${annee}-${String(mois).padStart(2, '0')}`;
}

/**
 * Éditeur rapide : popover ancré à la cellule (double-clic). Champ taux
 * pré-focalisé et pré-sélectionné — taper remplace directement la valeur.
 * Portée : dès ce mois sans fin / ce mois seulement / de ce mois à un mois
 * donné (un événement de retour ré-affirme la valeur précédente après la
 * plage, sauf si un changement existant prend déjà effet à ce moment-là).
 * Les projections ultérieures couvertes par la portée sont remplacées par la
 * valeur saisie (annoncé avant l'enregistrement) ; les définitifs, eux, ne
 * sont jamais écrasés.
 */
export function EditionRapide({ ligne, mois, annee, scenarioId, scenarios, ancre, onFermer, onModifie, onOuvrirFiche }: Props) {
  const cellule = ligne.mois[mois - 1];
  const [tauxPct, setTauxPct] = useState(String(Math.round(cellule.taux * 1000) / 10));
  const [nominal, setNominal] = useState(String(Math.round(cellule.nominal * 100) / 100));
  const [portee, setPortee] = useState<Portee>('sansFin');
  const [finMois, setFinMois] = useState(Math.min(mois + 1, 12));
  // Le statut par défaut suit la lecture affichée : on lit un scénario, on y
  // pose une hypothèse ; on lit les décisions seules, on arrête une décision.
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioId != null ? 'projete' : 'definitif');
  // Projeté ⇒ le scénario est obligatoire (contrainte DB et API)
  const [scenarioCible, setScenarioCible] = useState<number | null>(scenarioId);
  const [commentaireManuel, setCommentaireManuel] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const champTaux = useRef<HTMLInputElement>(null);

  useEffect(() => {
    champTaux.current?.focus();
    champTaux.current?.select();
  }, []);

  const tauxNum = Number(tauxPct) / 100;
  const nominalNum = Number(nominal);
  const tauxChange = Math.abs(tauxNum - cellule.taux) > 0.00004;
  const nominalChange = Math.abs(nominalNum - cellule.nominal) > 0.004;

  const porteeTexte = portee === 'sansFin'
    ? `dès ${MOIS_LABELS[mois - 1].toLowerCase()} ${annee}, sans fin`
    : portee === 'ceMois'
      ? `${MOIS_LABELS[mois - 1].toLowerCase()} ${annee} seulement`
      : `de ${MOIS_LABELS[mois - 1].toLowerCase()} à ${MOIS_LABELS[finMois - 1].toLowerCase()} ${annee}`;

  // Projections ultérieures dans la portée : elles masqueraient l'édition dès
  // leur mois d'effet — la valeur saisie les remplace (les définitifs restent).
  const moisFinPortee = portee === 'sansFin' ? 12 : portee === 'jusqua' ? finMois : mois;
  const impactTaux = tauxChange
    ? impactPortee(ligne.mois.map((c) => c.changementTaux?.tag), mois, moisFinPortee)
    : IMPACT_VIDE;
  const impactNominal = nominalChange
    ? impactPortee(ligne.mois.map((c) => c.changementNominal?.tag), mois, moisFinPortee)
    : IMPACT_VIDE;
  const impact = fusionnerImpacts(impactTaux, impactNominal);

  const commentaireAuto = useMemo(() => {
    const parts: string[] = [];
    if (tauxChange) parts.push(`taux ${fmtPct(cellule.taux)} → ${fmtPct(tauxNum)}`);
    if (nominalChange) parts.push(`nominal ${fmtCHF2(cellule.nominal)} → ${fmtCHF2(nominalNum)}`);
    return `Édition rapide : ${parts.length ? parts.join(', ') : 'sans changement'}, ${porteeTexte}`;
  }, [tauxChange, nominalChange, cellule.taux, cellule.nominal, tauxNum, nominalNum, porteeTexte]);

  const commentaire = commentaireManuel ?? commentaireAuto;

  const valide =
    (tauxChange || nominalChange) &&
    tauxNum >= 0 && tauxNum <= 3 && nominalNum >= 0 &&
    commentaire.trim().length > 0 &&
    (tag !== 'projete' || scenarioCible != null) &&
    (portee !== 'jusqua' || finMois >= mois);

  async function creerEvenements(champ: 'taux' | 'nominal', valeur: number) {
    const chemin = champ === 'taux' ? 'taux-evenements' : 'salaire-evenements';
    const cle = champ === 'taux' ? 'taux' : 'montant';
    const scenarioCorps = tag === 'projete' ? { scenarioId: scenarioCible } : {};
    await api.post(`/salaires/employes/${ligne.id}/${chemin}`, {
      moisEffet: iso(annee, mois), [cle]: valeur, tag, ...scenarioCorps,
      commentaire: commentaire.trim(),
    });
    // Les projections ultérieures de la portée reprendraient la main dès leur
    // mois d'effet : on les remplace par la même valeur, mois par mois.
    const aRemplacer = (champ === 'taux' ? impactTaux : impactNominal).aRemplacer;
    for (const m2 of aRemplacer) {
      await api.post(`/salaires/employes/${ligne.id}/${chemin}`, {
        moisEffet: iso(annee, m2), [cle]: valeur, tag, ...scenarioCorps,
        commentaire: `${commentaire.trim()} — remplace la projection de ${MOIS_LABELS[m2 - 1].toLowerCase()}`,
      });
    }
    if (portee === 'sansFin') return;

    const dernierMois = portee === 'ceMois' ? mois : finMois;
    const retourMois = dernierMois + 1;
    let moisEffetRetour: string;
    let valeurRetour: number;
    let dejaUnChangement = false;
    if (retourMois <= 12) {
      const cell = ligne.mois[retourMois - 1];
      valeurRetour = champ === 'taux' ? cell.taux : cell.nominal;
      dejaUnChangement = !!(champ === 'taux' ? cell.changementTaux : cell.changementNominal);
      moisEffetRetour = iso(annee, retourMois);
    } else {
      // La plage couvre décembre : le retour se pose en janvier de l'année suivante
      const cell = ligne.mois[11];
      valeurRetour = champ === 'taux' ? cell.taux : cell.nominal;
      moisEffetRetour = iso(annee + 1, 1);
    }
    if (!dejaUnChangement) {
      await api.post(`/salaires/employes/${ligne.id}/${chemin}`, {
        moisEffet: moisEffetRetour, [cle]: valeurRetour, tag, ...scenarioCorps,
        commentaire: `Fin d'application (${porteeTexte}) — retour à la valeur précédente`,
      });
    }
  }

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      if (tauxChange) await creerEvenements('taux', tauxNum);
      if (nominalChange) await creerEvenements('nominal', nominalNum);
      onModifie();
      onFermer();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  // Position : sous la cellule, au-dessus si la place manque. La hauteur est
  // **mesurée après rendu** (elle varie avec les avertissements de portée) et
  // le popover est toujours ramené dans la fenêtre — sans quoi la barre de
  // boutons passait sous le pli.
  const boite = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(ancre.bottom + 6);
  useLayoutEffect(() => {
    const el = boite.current;
    if (!el) return;
    const h = el.offsetHeight;
    const vise = ancre.bottom + 6 + h < window.innerHeight
      ? ancre.bottom + 6
      : Math.max(8, Math.min(ancre.top - h - 6, window.innerHeight - h - 8));
    setTop((prec) => (Math.abs(prec - vise) < 1 ? prec : vise));
  });
  const left = Math.min(Math.max(8, ancre.left - 80), window.innerWidth - LARGEUR - 12);

  return (
    <>
      <div className="edition-rapide__backdrop" onClick={onFermer} />
      <div
        ref={boite}
        className="edition-rapide"
        style={{ top, left, width: LARGEUR }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onFermer();
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'SELECT') enregistrer();
        }}
      >
        <div className="flex items-center justify-between mb-8">
          <div className="edition-rapide__titre" style={{ marginBottom: 0 }}>
            {ligne.nom} — {MOIS_LABELS[mois - 1]} {annee}
          </div>
          <button
            type="button"
            className="edition-rapide__fiche"
            onClick={onOuvrirFiche}
            title="Ouvrir la fiche complète (historique, répartition par fonction, infos)"
          >
            Fiche complète ↗
          </button>
        </div>

        <div className="flex gap-12 items-end mb-8">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Taux (%)</label>
            <input
              ref={champTaux}
              type="number"
              className="form-input"
              style={{ width: 90 }}
              min={0} max={300} step={0.5}
              value={tauxPct}
              onChange={(e) => setTauxPct(e.target.value)}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Nominal (100 %)</label>
            <input
              type="number"
              className="form-input"
              style={{ width: 110 }}
              min={0} step={0.05}
              value={nominal}
              onChange={(e) => setNominal(e.target.value)}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <div style={{ marginBottom: 2 }}>
            <ChoixTagScenario
              tag={tag}
              scenarioId={scenarioCible}
              scenarios={scenarios}
              onChange={(t, sc) => { setTag(t); setScenarioCible(sc); }}
            />
          </div>
        </div>

        <div className="edition-rapide__portee mb-8">
          <label className="edition-rapide__radio">
            <input type="radio" name="portee" checked={portee === 'sansFin'} onChange={() => setPortee('sansFin')} />
            <span>dès {MOIS_LABELS[mois - 1].toLowerCase()}, sans fin</span>
          </label>
          <label className="edition-rapide__radio">
            <input type="radio" name="portee" checked={portee === 'ceMois'} onChange={() => setPortee('ceMois')} />
            <span>{MOIS_LABELS[mois - 1].toLowerCase()} seulement</span>
          </label>
          <label className="edition-rapide__radio">
            <input type="radio" name="portee" checked={portee === 'jusqua'} onChange={() => setPortee('jusqua')} />
            <span>
              de {MOIS_LABELS[mois - 1].toLowerCase()} à{' '}
              <select
                className="sort-select"
                style={{ minWidth: 70 }}
                value={finMois}
                onChange={(e) => { setFinMois(Number(e.target.value)); setPortee('jusqua'); }}
              >
                {MOIS_LABELS.map((l, i) => i + 1 >= mois && <option key={i} value={i + 1}>{l}</option>)}
              </select>
            </span>
          </label>
        </div>

        <input
          className="form-input w-full mb-8"
          style={{ fontSize: 'var(--t-11)' }}
          title="Commentaire (pré-rempli, modifiable)"
          value={commentaire}
          onChange={(e) => setCommentaireManuel(e.target.value)}
        />

        {/* Les projections retenues (celles du budget) sont invisibles dans la
            lecture du tronc : l'impact de portée ci-dessous ne peut pas les
            annoncer, et elles reprendront la main dès leur mois d'effet. */}
        {scenarioId == null && moisFinPortee > mois && (
          <div className="edition-rapide__impact mb-8">
            <div className="edition-rapide__impact-warn">
              ⚠ Lecture « décidé seulement » : les hypothèses du budget n'y figurent pas et ne seront
              donc pas remplacées. Au-delà de {MOIS_LABELS[mois - 1].toLowerCase()}, la lecture
              « Budget » peut continuer d'afficher ses propres valeurs.
            </div>
          </div>
        )}

        {(impact.aRemplacer.length > 0 || impact.proteges.length > 0) && (
          <div className="edition-rapide__impact mb-8">
            {impact.aRemplacer.length > 0 && (
              <div className="edition-rapide__impact-warn">
                ⚠ Remplace aussi {impact.aRemplacer.length > 1 ? 'les projections' : 'la projection'} de {listerMois(impact.aRemplacer)}.
              </div>
            )}
            {impact.proteges.length > 0 && (
              <div className="text-ink-3">
                {impact.proteges.length > 1 ? 'Les définitifs' : 'Le définitif'} de {listerMois(impact.proteges)} {impact.proteges.length > 1 ? 'restent' : 'reste'} en vigueur.
              </div>
            )}
          </div>
        )}

        {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}

        <div className="flex items-center justify-between">
          <span className="text-xs text-ink-3">
            {tauxChange || nominalChange
              ? <>Mensuel : <strong>CHF {fmtCHF2(nominalNum * tauxNum)}</strong></>
              : 'Modifier une valeur…'}
          </span>
          <div className="flex gap-8">
            <button className="btn btn-sm" onClick={onFermer}>Annuler</button>
            <button className="btn btn-accent btn-sm" disabled={!valide || enCours} onClick={enregistrer}>
              {enCours ? 'Enregistrement…' : 'OK ⏎'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
